import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowUpRight, Linkedin, Facebook, Github } from "lucide-react";
import { useSite } from "@/lib/site";

export default function Footer() {
  const { site } = useSite();
  const offices = site?.offices || [];
  const contact = site?.contact || {};
  const footer = site?.footer || {};
  const brandName = site?.brand?.name || "Miaki";

  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-background/90">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-3">
          <div className="flex items-center gap-2">
            {site?.brand?.logo ? (
              <div>
                {site.brand.logo_dark ? (
                  <>
                    <img src={site.brand.logo_dark} alt="Logo" width="128" height="32" className="h-8 w-auto object-contain dark:hidden block" />
                    <img src={site.brand.logo} alt="Logo" width="128" height="32" className="h-8 w-auto object-contain hidden dark:block" />
                  </>
                ) : (
                  <img src={site.brand.logo} alt="Logo" width="128" height="32" className="h-8 w-auto object-contain" />
                )}
              </div>
            ) : (
              <div>
                <img src={`${process.env.PUBLIC_URL}/images/miaki_dark.png`} alt={brandName} width="128" height="32" className="h-8 w-auto object-contain dark:hidden block" />
                <img src={`${process.env.PUBLIC_URL}/images/miaki_white.png`} alt={brandName} width="128" height="32" className="h-8 w-auto object-contain hidden dark:block" />
              </div>
            )}
          </div>
          {footer.tagline && (
            <p className="mt-5 text-sm text-foreground/65 max-w-sm leading-relaxed">{footer.tagline}</p>
          )}
          <div className="mt-6 flex items-center gap-4">
            {site?.social?.linkedin && (
              <a href={site.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="Miaki on LinkedIn" className="text-foreground/40 hover:text-primary transition-colors">
                <Linkedin className="w-5 h-5" aria-hidden="true" />
              </a>
            )}
            {site?.social?.facebook && (
              <a href={site.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Miaki on Facebook" className="text-foreground/40 hover:text-primary transition-colors">
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
            )}
            {site?.social?.github && (
              <a href={site.social.github} target="_blank" rel="noopener noreferrer" aria-label="Miaki on GitHub" className="text-foreground/40 hover:text-primary transition-colors">
                <Github className="w-5 h-5" aria-hidden="true" />
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">Services</p>
          <ul className="mt-4 space-y-2 text-sm text-foreground/75">
            <li><Link to="/ai-solutions" className="hover:text-primary">AI Solutions</Link></li>
            <li><Link to="/software-engineering" className="hover:text-primary">Software Engineering</Link></li>
            <li><Link to="/staff-augmentation" className="hover:text-primary">Staff Augmentation</Link></li>
            <li><Link to="/industries" className="hover:text-primary">Industries</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">Company</p>
          <ul className="mt-4 space-y-2 text-sm text-foreground/75">
            <li><Link to="/about" className="hover:text-primary">About</Link></li>
            <li><Link to="/careers" className="hover:text-primary">Careers</Link></li>
            {/* <li><Link to="/case-studies" className="hover:text-primary">Case Studies</Link></li> */}
            <li><Link to="/insights" className="hover:text-primary">Insights</Link></li>
            <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
            {/* <li><Link to="/faq" className="hover:text-primary">FAQ</Link></li> */}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">Offices</p>
          <ul className="mt-4 space-y-1 text-sm text-foreground/75">
            {offices.map((o) => (
              <li key={o.city} className="flex gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-foreground/40 shrink-0" aria-hidden="true" />
                <span>
                  {o.address},<br />{o.city}, {o.country}
                </span>
              </li>
            ))}
            {contact.email && (
              <li className="flex gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-foreground/40 shrink-0" />
                <a href={`mailto:${contact.email}`} className="hover:text-primary" data-testid="footer-email">
                  {contact.email}
                </a>
              </li>
            )}
            {contact.phone && (
              <li className="flex gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-foreground/40 shrink-0" />
                <div className="space-y-1" data-testid="footer-phone">
                  {contact.phone.split(",").map((p, idx) => {
                    const cleanPhone = p.trim();
                    if (!cleanPhone) return null;
                    return (
                      <a
                        key={idx}
                        href={`tel:${cleanPhone.replace(/\s+/g, "")}`}
                        className="block hover:text-primary"
                      >
                        {cleanPhone}
                      </a>
                    );
                  })}
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {brandName}. {footer.copyright || "Creating Happiness."}</p>
          <p className="font-mono">{site?.brand?.tagline || ""}</p>
        </div>
      </div>
    </footer>
  );
}
